import { type ReactElement, useId } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import { Box, InlineError, Stack, Text } from '@/ui';
import { TextField } from '@/ui/primitives/TextField';
import type { FieldErrorProps } from '@/ui/hooks/useFormErrors';

type AddProjectDialogProps = {
  open: boolean;
  saving?: boolean;
  required?: boolean;
  title?: string;
  confirmLabel?: string;
  name: string;
  errorMessage?: string | null;
  nameErrorProps?: FieldErrorProps;
  onNameChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
};

export const AddProjectDialog = ({
  open,
  saving = false,
  required = false,
  title = 'Add Project',
  confirmLabel = 'Add Project',
  name,
  errorMessage,
  nameErrorProps,
  onNameChange,
  onClose,
  onSave,
}: AddProjectDialogProps): ReactElement => {
  const nameId = useId();
  const isSaveDisabled = name.trim().length === 0;
  const spacingSm = s('sm');
  const Field = TextField;
  const singleLineFieldSx = {
    '& .MuiInputBase-root': {
      backgroundColor: 'background.default',
      minHeight: `${componentSizes.auth.inputHeight}px !important`,
      height: `${componentSizes.auth.inputHeight}px !important`,
      maxHeight: `${componentSizes.auth.inputHeight}px !important`,
      boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-root': {
      minHeight: `${componentSizes.auth.inputHeight}px !important`,
      height: `${componentSizes.auth.inputHeight}px !important`,
    },
  } as const;
  return (
    <ConfirmDialog
      open={open}
      title={title}
      confirmLabel={confirmLabel}
      confirmColor="primary"
      confirmDisabled={isSaveDisabled}
      loading={saving}
      onClose={onClose}
      onConfirm={onSave}
      hideCancel={required}
      disableClose={required}
      allowTopBarInteraction={required}
      description={
        <Stack gap="md">
          {required ? (
            <Text role="body" sx={{ color: 'text.secondary' }}>
              Get started by adding your first project. You can rename it later.
            </Text>
          ) : null}
          <Box>
            <Box sx={{ mb: spacingSm }}>
              <Text role="body" component="label" htmlFor={nameId}>
                Project Name
              </Text>
            </Box>
            <Field
              id={nameId}
              value={name}
              onChange={(event) => {
                onNameChange(event.target.value);
              }}
              placeholder="My Project"
              required
              autoFocus
              fullWidth
              size="small"
              sx={singleLineFieldSx}
              {...(nameErrorProps ?? {})}
            />
          </Box>
          {errorMessage ? <InlineError message={errorMessage} /> : null}
        </Stack>
      }
      maxWidth="xs"
    />
  );
};
